import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Clock, CalendarBlank, Swap, ClipboardText, AirplaneTilt } from '@phosphor-icons/react';
import OvertimeFormModal from '@/components/forms/OvertimeFormModal';
import LeaveFormModal from '@/components/forms/LeaveFormModal';
import LeaveTransferFormModal from '@/components/forms/LeaveTransferFormModal';
import WorkDeclarationFormModal from '@/components/forms/WorkDeclarationFormModal';
import TravelExpenseFormModal from '@/components/forms/TravelExpenseFormModal';

const formCards = [
  {
    id: 'overtime',
    title: '勤務指示書',
    description: '残業・休日出勤・早出・直行直帰などの勤務指示',
    icon: Clock,
    color: 'from-blue-500 to-blue-600',
  },
  {
    id: 'leave',
    title: '休暇届',
    description: '有給・特別休暇や欠勤などの届出',
    icon: CalendarBlank,
    color: 'from-emerald-500 to-emerald-600',
  },
  {
    id: 'leave_transfer',
    title: '振替休暇申請書',
    description: '休日と勤務日の振替を申請',
    icon: Swap,
    color: 'from-purple-500 to-purple-600',
  },
  {
    id: 'work_declaration',
    title: '勤務時間申告書',
    description: '打刻漏れなど勤怠データ不備の申告',
    icon: ClipboardText,
    color: 'from-amber-500 to-amber-600',
  },
  {
    id: 'travel_expense',
    title: '出張旅費精算書',
    description: '出張の交通費・宿泊費などの精算',
    icon: AirplaneTilt,
    color: 'from-rose-500 to-rose-600',
  },
];

export default function Dashboard() {
  const [activeForm, setActiveForm] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Auto-open form when ?form=... is present in the URL
  useEffect(() => {
    const f = searchParams.get('form');
    if (f && formCards.some((c) => c.id === f)) {
      setActiveForm(f);
      // Clear the param so closing/reopening works normally
      searchParams.delete('form');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const openForm = (formId) => setActiveForm(formId);
  const closeForm = () => setActiveForm(null);

  return (
    <>
    <div className="page-enter">
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl font-bold tracking-tight text-zinc-900 mb-2">
          申請フォーム
        </h1>
        <p className="text-sm text-zinc-500 leading-relaxed">
          各種申請フォームを選択してください。申請後、自動で担当者に通知メールが送信されます。
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 stagger-children">
        {formCards.map((card) => {
          const Icon = card.icon;
          return (
            <button
              key={card.id}
              onClick={() => openForm(card.id)}
              data-testid={`form-card-${card.id}`}
              className="interactive-card group relative bg-white border border-zinc-200 rounded-lg p-6 text-left active:scale-[0.98] hover:border-teal-500 transition-[border-color,box-shadow]"
            >
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 rounded-lg bg-zinc-100 flex items-center justify-center flex-shrink-0">
                  <Icon size={24} weight="duotone" className="text-teal-700" />
                </div>
                <div className="flex-1">
                  <h3 className="text-base font-medium text-zinc-900 mb-1 group-hover:text-teal-700 transition-colors">
                    {card.title}
                  </h3>
                  <p className="text-xs text-zinc-500 leading-relaxed">{card.description}</p>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>

      {/* Modals rendered OUTSIDE .page-enter: that wrapper keeps a retained
          transform (animation fill-mode: both), which would trap the fixed
          overlay inside the content area. Kept inline (no portal) so mouse
          clicks work in all environments. */}
      {activeForm === 'overtime' && <OvertimeFormModal onClose={closeForm} />}
      {activeForm === 'leave' && <LeaveFormModal onClose={closeForm} />}
      {activeForm === 'leave_transfer' && <LeaveTransferFormModal onClose={closeForm} />}
      {activeForm === 'work_declaration' && <WorkDeclarationFormModal onClose={closeForm} />}
      {activeForm === 'travel_expense' && <TravelExpenseFormModal onClose={closeForm} />}
    </>
  );
}
